import {
  AlertTriangle,
  BadgeDollarSign,
  Building2,
  ClipboardList,
  FileText,
  LayoutDashboard,
  Package,
  Receipt,
  Settings,
  ShoppingCart,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { evoraLogoMask } from "@/assets/evora-logo-mask";
import { evoraLogoPaths } from "@/assets/evora-logo-paths";
import { selectCurrentUser } from "@/features/auth";
import { useAppSelector } from "@/shared/store/hooks";

import { LINK_TO_PATH } from "./navConfig";

function iconNameFromApi(icone: string): string {
  return icone
    .replace(/^lucide-/, "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}

// ─── Evora Maxx Logo (from Figma) ────────────────────────────────────────────

function EvoraLogo() {
  return (
    <div
      style={{
        position: "relative",
        width: 75,
        height: 30,
        overflow: "hidden",
      }}
      data-name="Logotipo/Evora"
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          maskImage: `url('${evoraLogoMask}')`,
          WebkitMaskImage: `url('${evoraLogoMask}')`,
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskSize: "73.426px 30.069px",
          WebkitMaskSize: "73.426px 30.069px",
          maskPosition: "0px -0.001px",
          WebkitMaskPosition: "0px -0.001px",
        }}
      >
        <svg
          style={{ display: "block", width: "100%", height: "100%" }}
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 73.4257 30.0698"
        >
          <path d={evoraLogoPaths.p32d13000} fill="#034065" />
          <path d={evoraLogoPaths.p35008480} fill="#034065" />
          <path d={evoraLogoPaths.p870c730} fill="#034065" />
          <path d={evoraLogoPaths.p1748ea00} fill="#034065" />
          <path d={evoraLogoPaths.p1a209180} fill="#034065" />
          <path d={evoraLogoPaths.p7dd4b00} fill="#B89B3C" />
          <path d={evoraLogoPaths.p3e474a00} fill="#034065" />
          <path d={evoraLogoPaths.p3ba30a80} fill="#034065" />
          <path d={evoraLogoPaths.pfbf7680} fill="#034065" />
          <path d={evoraLogoPaths.p14f28280} fill="#B89B3C" />
          <path d={evoraLogoPaths.p341ff580} fill="#B89B3C" />
          <path d={evoraLogoPaths.p2beb97c0} fill="#034065" />
          <path d={evoraLogoPaths.p2dc46580} fill="#034065" />
          <path d={evoraLogoPaths.p1449bf00} fill="#034065" />
          <path d={evoraLogoPaths.p2e172b00} fill="#034065" />
          <path d={evoraLogoPaths.pb120440} fill="#034065" />
          <path d={evoraLogoPaths.p1c72e300} fill="#034065" />
          <path d={evoraLogoPaths.p3facdd00} fill="#034065" />
          <path d={evoraLogoPaths.p21573f0} fill="#B89B3C" />
          <path d={evoraLogoPaths.p2bb37571} fill="#B89B3C" />
          <path d={evoraLogoPaths.peff1500} fill="#034065" />
          <path d={evoraLogoPaths.p166a0500} fill="#034065" />
        </svg>
      </div>
    </div>
  );
}

// ─── Hamburger icon ───────────────────────────────────────────────────────────

function HamburgerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M2.667 4h10.666"
        stroke="#99A1AF"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.667 8h10.666"
        stroke="#99A1AF"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.667 12h10.666"
        stroke="#99A1AF"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Chevron ─────────────────────────────────────────────────────────────────

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 7"
      fill="none"
      className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    >
      <path
        d={evoraLogoPaths.p324f7a80}
        stroke="#99A1AF"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.083"
      />
    </svg>
  );
}

// ─── Icon resolver ────────────────────────────────────────────────────────────

const lucideIconMap: Record<string, React.ElementType> = {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FileText,
  Wrench,
  BadgeDollarSign,
  Receipt,
  Building2,
  ClipboardList,
  Users,
  Settings,
  AlertTriangle,
};

function NavIcon({ name, active }: { name: string; active?: boolean }) {
  const Icon = lucideIconMap[name];
  if (!Icon) return null;
  return (
    <Icon size={16} color={active ? "#ffffff" : "#99A1AF"} strokeWidth={1.5} />
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({
  collapsed,
  onToggleCollapse,
  isMobile,
  mobileOpen,
  onCloseMobile,
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // auto-expand parent whose child matches current path on route change
  useEffect(() => {
    (user?.menus ?? []).forEach((item) => {
      if (
        item.submenus.some(
          (sub) => LINK_TO_PATH[sub.link] === location.pathname
        )
      ) {
        setExpandedItems((prev) =>
          prev.includes(String(item.id)) ? prev : [...prev, String(item.id)]
        );
      }
    });
  }, [location.pathname, user?.menus]);

  // Fecha o drawer no mobile ao navegar
  useEffect(() => {
    if (isMobile && mobileOpen) {
      onCloseMobile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const sidebarContent = (
    <aside
      className={`flex h-full shrink-0 flex-col border-r border-[#E4E4E4] bg-white ${
        isMobile
          ? "w-max min-w-[223px] max-w-[85vw]"
          : `transition-all duration-300 ${collapsed ? "w-16" : "w-max min-w-[223px]"}`
      }`}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="relative h-[62.75px] shrink-0 border-b border-[#f3f4f6]">
        {(!collapsed || isMobile) && (
          <div className="absolute top-[16px] left-[16px]">
            <EvoraLogo />
          </div>
        )}
        {/* Botão fechar no mobile OU hamburger no desktop */}
        {isMobile ? (
          <button
            onClick={onCloseMobile}
            aria-label="Fechar menu"
            className="absolute top-[22px] right-[16px] transition-opacity hover:opacity-60"
          >
            <X size={16} color="#99A1AF" />
          </button>
        ) : (
          <button
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            className={`absolute top-[23px] transition-opacity hover:opacity-60 ${
              collapsed ? "left-1/2 -translate-x-1/2" : "right-[16px]"
            }`}
          >
            <HamburgerIcon />
          </button>
        )}
      </div>

      {/* ── Nav ────────────────────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-3" aria-label="Menu principal">
        {(user?.menus ?? []).map((item) => {
          const itemPath = item.link ? LINK_TO_PATH[item.link] : undefined;
          const hasChildren = item.submenus.length > 0;
          const isActive = itemPath
            ? location.pathname === itemPath
            : item.submenus.some(
                (sub) => LINK_TO_PATH[sub.link] === location.pathname
              );
          const isExpanded = expandedItems.includes(String(item.id));
          const iconName = iconNameFromApi(item.icone);

          return (
            <div key={item.id}>
              {/* ── Parent / leaf row */}
              <div className="mx-2">
                <button
                  onClick={() => {
                    if (hasChildren) {
                      toggleExpand(String(item.id));
                    } else if (itemPath) {
                      navigate(itemPath);
                    }
                  }}
                  title={collapsed ? item.nome : undefined}
                  className={`flex items-center justify-center rounded-[8px] transition-colors ${
                    collapsed
                      ? "w-full py-[16px]"
                      : "w-full gap-[10px] px-[10px] py-[16px]"
                  } ${isActive ? "bg-[#D1BE80]" : "hover:bg-[#f4f6f8]"}`}
                >
                  {/* Icon */}
                  <span className="flex size-[16px] shrink-0 items-center justify-center">
                    <NavIcon name={iconName} active={isActive} />
                  </span>

                  {/* Label + chevron */}
                  {(!collapsed || isMobile) && (
                    <>
                      <span
                        className={`min-w-0 flex-1 text-left text-[13px] leading-[19.5px] font-medium tracking-[-0.076px] whitespace-nowrap ${
                          isActive ? "text-white" : "text-[#4a5565]"
                        }`}
                      >
                        {item.nome}
                      </span>
                      {hasChildren && <ChevronDown open={isExpanded} />}
                    </>
                  )}
                </button>
              </div>

              {/* ── Children */}
              {(!collapsed || isMobile) && hasChildren && isExpanded && (
                <div className="relative mx-2 mt-0 mb-1 ml-[26px] flex flex-col gap-[2px] border-l border-[#f3f4f6] pl-[13px]">
                  {item.submenus.map((sub) => {
                    const subPath = LINK_TO_PATH[sub.link];
                    if (!subPath) return null;
                    const childActive = location.pathname === subPath;
                    return (
                      <button
                        key={sub.id}
                        onClick={() => navigate(subPath)}
                        className={`h-[30px] w-full rounded-[8px] px-[8px] text-left transition-colors ${
                          childActive
                            ? "bg-[#E5EEF4] text-[#034065]"
                            : "text-[#6a7282] hover:bg-[#f4f6f8] hover:text-[#034065]"
                        }`}
                      >
                        <span className="text-[12px] leading-[18px] font-medium whitespace-nowrap">
                          {sub.nome}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );

  // No mobile renderiza como drawer com backdrop
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={onCloseMobile}
            aria-hidden="true"
          />
        )}
        {/* Drawer */}
        <div
          className={`fixed inset-y-0 left-0 z-50 transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {sidebarContent}
        </div>
      </>
    );
  }

  return sidebarContent;
}
